// Supabase CRUD for product catalogue images (table: "catalogue_images").
import { supabase } from "../supabase/client";

// Only these app fields differ from their DB column names.
const APP_TO_DB = {
  imageUrl: "image_url",
  sortOrder: "sort_order",
  productKey: "product_key",
};

// DB (snake_case) → app (camelCase)
const fromRow = (r) => ({
  id: r.id,
  category: r.category,
  imageUrl: r.image_url,
  caption: r.caption,
  sortOrder: r.sort_order,
  active: r.active,
  // Images sharing a key are views of one product. null = its own product.
  productKey: r.product_key || null,
});

// app → DB, mapping only the keys actually provided, so partial updates
// (like the active toggle) don't null out other columns.
const toRow = (d) => {
  const out = {};
  for (const [k, v] of Object.entries(d)) out[APP_TO_DB[k] || k] = v;
  return out;
};

/** Every image, admin view — includes hidden ones. */
export async function getCatalogueImages() {
  const { data, error } = await supabase
    .from("catalogue_images")
    .select("*")
    .order("category", { ascending: true })
    .order("sort_order", { ascending: true })
    .order("created_at", { ascending: true });
  if (error) throw error;
  return data.map(fromRow);
}

/**
 * Active images only, grouped by category slug — one query for the whole
 * Sourcing page, so opening a catalogue needs no further round trip.
 * @returns {Promise<Record<string, Array>>}
 */
export async function getCatalogueByCategory() {
  const { data, error } = await supabase
    .from("catalogue_images")
    .select("*")
    .eq("active", true)
    .order("sort_order", { ascending: true })
    .order("created_at", { ascending: true });
  if (error) throw error;

  const grouped = {};
  for (const row of data) {
    const img = fromRow(row);
    (grouped[img.category] = grouped[img.category] || []).push(img);
  }
  return grouped;
}

export async function addCatalogueImage(data) {
  const { error } = await supabase.from("catalogue_images").insert(toRow(data));
  if (error) throw error;
}

/** Insert several at once — the admin uploader accepts multiple files. */
export async function addCatalogueImages(rows) {
  if (!rows.length) return;
  const { error } = await supabase
    .from("catalogue_images")
    .insert(rows.map(toRow));
  if (error) throw error;
}

export async function updateCatalogueImage(id, data) {
  const { error } = await supabase
    .from("catalogue_images")
    .update(toRow(data))
    .eq("id", id);
  if (error) throw error;
}

export async function deleteCatalogueImage(id) {
  const { error } = await supabase.from("catalogue_images").delete().eq("id", id);
  if (error) throw error;
}

/**
 * Collapse a category's images into products.
 *
 * Images that share a productKey are one product; an image without a key is a
 * product on its own. The caller passes images already ordered by sortOrder, so
 * a product takes the position of its first image and that image is the cover.
 *
 * Pure — no Supabase — so it can be unit tested and reused by both the public
 * catalogue and the admin panel.
 *
 * @param {Array} images
 * @returns {Array<{key: string, cover: object, images: Array, caption: string|null}>}
 */
export function groupIntoProducts(images) {
  const products = [];
  const byKey = new Map();

  for (const img of images || []) {
    if (!img.productKey) {
      // Its own product. Keyed by image id so React keys stay stable.
      products.push({ key: `single:${img.id}`, cover: img, images: [img] });
      continue;
    }
    let p = byKey.get(img.productKey);
    if (!p) {
      p = { key: `group:${img.productKey}`, cover: img, images: [] };
      byKey.set(img.productKey, p);
      products.push(p);
    }
    p.images.push(img);
  }

  // A product's caption is its cover's, falling back to the first view that has
  // one — so a group still reads properly when only a detail shot is captioned.
  for (const p of products) {
    p.caption = p.cover.caption || p.images.find((i) => i.caption)?.caption || null;
  }
  return products;
}

/**
 * Rewrite sort_order across a category in one request.
 *
 * Grouping only works if a product's images sit together in the order, so every
 * structural change in the admin panel renumbers the whole category rather than
 * trying to patch two rows and hope they stay adjacent.
 *
 * category and imageUrl are sent because they are NOT NULL: an upsert has to
 * present a row that would be valid as an insert, even when the conflict always
 * routes it to an update. Columns left out (caption, active, product_key) are
 * not touched.
 */
export async function setCatalogueSortOrder(images) {
  if (!images.length) return;
  const rows = images.map((img, i) => ({
    id: img.id,
    category: img.category,
    image_url: img.imageUrl,
    sort_order: i + 1,
  }));
  const { error } = await supabase
    .from("catalogue_images")
    .upsert(rows, { onConflict: "id" });
  if (error) throw error;
}

/** Put several images into one product (or clear the grouping with null). */
export async function setProductKey(ids, productKey) {
  if (!ids.length) return;
  const { error } = await supabase
    .from("catalogue_images")
    .update({ product_key: productKey })
    .in("id", ids);
  if (error) throw error;
}

// ─── Ordering helpers (pure) ────────────────────────────────────────────────
// Grouping only reads correctly if a product's views sit together in the
// order, so every structural change recomputes the whole category's sequence.
// These work out that sequence; setCatalogueSortOrder writes it.
//
// Each one places a block of images at the position of its first member,
// counting only the images that remain — so they hold up even if a product's
// views were scattered through the order to begin with.

/** Where a block lands once its own members are removed from the list. */
function insertPoint(list, isMember) {
  const firstIdx = list.findIndex(isMember);
  if (firstIdx < 0) return list.length;
  return list.slice(0, firstIdx).filter((i) => !isMember(i)).length;
}

/** Bring the selected images together, at the position of the first of them. */
export function orderWithGrouped(shown, selectedIds) {
  const ids = new Set(selectedIds);
  const isMember = (i) => ids.has(i.id);
  const block = shown.filter(isMember);
  const rest = shown.filter((i) => !isMember(i));
  const at = insertPoint(shown, isMember);
  return [...rest.slice(0, at), ...block, ...rest.slice(at)];
}

/** Promote one view to the front of its product, keeping the product in place. */
export function orderWithCover(shown, product, imageId) {
  const memberIds = new Set(product.images.map((i) => i.id));
  const isMember = (i) => memberIds.has(i.id);
  const chosen = product.images.find((i) => i.id === imageId);
  if (!chosen) return shown;
  const block = [chosen, ...product.images.filter((i) => i.id !== imageId)];
  const rest = shown.filter((i) => !isMember(i));
  const at = insertPoint(shown, isMember);
  return [...rest.slice(0, at), ...block, ...rest.slice(at)];
}

/** Swap a whole product with its neighbour, views and all. */
export function orderWithProductMoved(products, index, dir) {
  const target = index + dir;
  if (index < 0 || index >= products.length || target < 0 || target >= products.length) {
    return products.flatMap((p) => p.images);
  }
  const next = [...products];
  [next[index], next[target]] = [next[target], next[index]];
  return next.flatMap((p) => p.images);
}
